import React from "react";
import { useGauge } from "use-gauge";
import { convertPxToRem } from "../../../../utils";
import theme from "../../../../theme";
import {
  FeatureToggleIdentifier,
  isFeatureEnabled,
} from "@/services/feature-toggle-adapter";

interface HealthMeasurementGaugeDataValueRange {
  min: number;
  max: number;
  healthRiskIndicator: string;
  color: string;
}

interface Props {
  value: number;
  unit?: string;
  valueRange: HealthMeasurementGaugeDataValueRange[];
  numberOfTicks: number;
  healthMeasurementIdentifier: string;
  tickValues?: number[];
  tickLabelValues?: number[];
  /** When provided, maps value to angle (degrees) for non-linear scale. Needle position is derived from this. */
  valueToAngle?: (value: number) => number;
}

const GAUGE_OPTIONS = {
  START_ANGLE: 90,
  END_ANGLE: 270,
  DIAMETER: 215,
  CHART_SECTION_OFFSET: 30,
  CHART_SECTION_STROKE_WIDTH: 24,
};

function fillWithCurrentMeasurementColor(
  value: number,
  valueRange: HealthMeasurementGaugeDataValueRange[]
): string | undefined {
  const maxValueRange = valueRange.reduce((prev, curr) =>
    curr.max > prev.max ? curr : prev
  );

  if (value > maxValueRange.max) {
    return maxValueRange.color;
  }

  const rangeColor =
    valueRange.find((range) => value >= range.min && value <= range.max)
      ?.color || undefined;

  return rangeColor;
}

function linearAngleToValue(
  angle: number,
  min: number,
  max: number,
  startAngle: number,
  endAngle: number
): number {
  return min + ((max - min) * (angle - startAngle)) / (endAngle - startAngle);
}

export default function HealthMeasurementGauge({
  value,
  numberOfTicks,
  valueRange,
  healthMeasurementIdentifier,
  tickValues,
  tickLabelValues,
  valueToAngle: customValueToAngle,
}: Props) {
  const sortedValueRange = [...valueRange].toSorted((a, b) => b.max - a.max);
  const min = sortedValueRange[sortedValueRange.length - 1].min;
  const isTrendedVitalsEnabled = isFeatureEnabled(
    FeatureToggleIdentifier.ENABLE_TRENDED_VITALS
  );
  let max = sortedValueRange[0].max;

  if (value > max) {
    max = value;
  }
  const gauge = useGauge({
    domain: [min, max],
    startAngle: GAUGE_OPTIONS.START_ANGLE,
    endAngle: GAUGE_OPTIONS.END_ANGLE,
    diameter: GAUGE_OPTIONS.DIAMETER,
    numTicks: numberOfTicks,
  });

  const valueToAngle =
    customValueToAngle ?? ((v: number) => gauge.valueToAngle(v));
  const needleValue = customValueToAngle
    ? linearAngleToValue(
        customValueToAngle(value),
        min,
        max,
        GAUGE_OPTIONS.START_ANGLE,
        GAUGE_OPTIONS.END_ANGLE
      )
    : value;

  const needle = gauge.getNeedleProps({
    value: needleValue,
    baseRadius: 12,
    tipRadius: 4,
  });

  const { width, height, viewBox } = gauge.getSVGProps();

  return (
    <div
      role="meter"
      aria-label={healthMeasurementIdentifier}
      aria-valuenow={value}
      style={{ padding: "1rem", width: "fit-content" }}
    >
      <svg
        style={{ overflow: "visible", padding: "1rem" }}
        viewBox={viewBox}
        height={height}
        width={width}
      >
        <g>
          <path
            {...gauge.getArcProps({
              offset: GAUGE_OPTIONS.CHART_SECTION_OFFSET,
              startAngle: GAUGE_OPTIONS.START_ANGLE,
              endAngle: GAUGE_OPTIONS.END_ANGLE,
            })}
            fill="none"
            stroke={fillWithCurrentMeasurementColor(value, sortedValueRange)}
            strokeLinecap="round"
            strokeWidth={GAUGE_OPTIONS.CHART_SECTION_STROKE_WIDTH}
          />
          {sortedValueRange.map((el) => {
            return (
              <path
                key={`${el.min}-${el.max}`}
                {...gauge.getArcProps({
                  offset: GAUGE_OPTIONS.CHART_SECTION_OFFSET,
                  startAngle: valueToAngle(el.min),
                  endAngle: valueToAngle(el.max),
                })}
                fill="none"
                stroke={el.color}
                strokeLinecap="round"
                strokeWidth={GAUGE_OPTIONS.CHART_SECTION_STROKE_WIDTH}
              />
            );
          })}
        </g>
        <g>
          {(tickValues ?? gauge.ticks.map((a) => gauge.angleToValue(a))).map(
            (tickValue) => {
              const angle = valueToAngle(tickValue);
              const asValue = tickValue;
              const highlightTick = Math.round(value) === Math.round(asValue);
              const showTickLabel = tickLabelValues
                ? tickLabelValues.some(
                    (v) => Math.round(v) === Math.round(asValue)
                  )
                : !!sortedValueRange.find((el) => {
                    if (Math.round(el.min) === Math.round(asValue)) return true;
                    if (Math.round(el.max) === Math.round(asValue)) return true;
                    return false;
                  });

              return (
                <React.Fragment key={`tick-group-${angle}-${asValue}`}>
                  <line
                    stroke={
                      highlightTick
                        ? fillWithCurrentMeasurementColor(
                            value,
                            sortedValueRange
                          )
                        : theme.palette.primary[500]
                    }
                    strokeWidth={showTickLabel || highlightTick ? 2 : 0.5}
                    strokeLinecap="round"
                    {...gauge.getTickProps({ angle, length: 6 })}
                  />
                  {showTickLabel && (
                    <text
                      style={{
                        fontSize: convertPxToRem(12),
                        lineHeight: convertPxToRem(10),
                        fontWeight: 600,
                        fill: theme.palette.primary[500],
                      }}
                      {...(gauge.getLabelProps({
                        angle,
                        offset: 12,
                      }) as React.SVGTextElementAttributes<SVGTextElement>)}
                    >
                      {Math.round(asValue)}
                    </text>
                  )}
                </React.Fragment>
              );
            }
          )}
        </g>
        <g>
          <circle
            fill={theme.palette.background.paper}
            {...needle.base}
            r={20}
          />
          {isTrendedVitalsEnabled ? (
            (() => {
              const arrowColor = fillWithCurrentMeasurementColor(
                value,
                sortedValueRange
              );
              const x2 = needle.tip.cx * 0.9;
              const y2 = needle.tip.cy * 0.9;

              // Calculate arrow direction angle (in degrees)
              const angleRad = Math.atan2(
                y2 - needle.base.cy,
                x2 - needle.base.cx
              );
              const angleDeg = (angleRad * 180) / Math.PI;

              return (
                <path
                  d="M0.00147882 13.3321L0.585558 1.16357C0.628298 0.273157 1.58015 -0.272551 2.37015 0.140449L13.1664 5.78456C14.2342 6.34282 13.9331 7.94649 12.7354 8.07929L6.68178 8.75055C6.34537 8.78786 6.03956 8.96318 5.83739 9.23465L2.19951 14.1196C1.47979 15.0861 -0.0562934 14.5357 0.00147882 13.3321Z"
                  fill={arrowColor}
                  transform={`translate(${x2}, ${y2}) rotate(${angleDeg + 120}) translate(-7, -7.5)`}
                />
              );
            })()
          ) : (
            <>
              <circle
                fill={theme.palette.primary[300]}
                cx={needle.base.cx}
                cy={needle.base.cy}
                r={10}
              />
              <line
                stroke={theme.palette.primary[300]}
                strokeLinecap="round"
                strokeWidth={3}
                x1={needle.base.cx}
                x2={needle.tip.cx * 0.9}
                y1={needle.base.cy}
                y2={needle.tip.cy * 0.9}
              />
            </>
          )}
        </g>
        <text
          x={needle.base.cx}
          y={isTrendedVitalsEnabled ? needle.base.cy : needle.base.cy + 50}
          textAnchor="middle"
          style={{
            fontSize: convertPxToRem(36),
            lineHeight: convertPxToRem(39),
            fontWeight: 700,
            fill: fillWithCurrentMeasurementColor(value, sortedValueRange),
          }}
        >
          {value}
        </text>
      </svg>
    </div>
  );
}
